import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AfterViewInit, Component, inject, LOCALE_ID, OnInit } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { AccountService, CompanySwitcherComponent } from '@visiativ/digital-account-front';
import { BookmarkListComponent } from '@visiativ/digital-bookmark-front';
import { AvatarComponent, StoreService } from '@visiativ/digital-components-kit';
import {
	UikSidenavLayoutDirective,
	UikSidenavLogoComponent,
	UikSidenavNavigationRouteComponent,
	UikSidenavService,
	UikSidenavSubNavigationComponent,
	UikSidenavSubNavigationRefDirective,
	UikSubNavigationComponent,
} from '@visiativ/uik-am';
import { catchError, forkJoin, map, Observable, of, Subject, takeUntil } from 'rxjs';
import { isActiveLink } from '../../../core/utils/utils';
import { Company } from '../../interfaces/company.interface';
import { Link } from '../../interfaces/navbar.interface';
import { Profile } from '../../interfaces/profile.interface';
import { ProfileMenuBottomSheetComponent } from '../bottom-sheets/user-profile-menu-bottom-sheet/user-profile-menu-bottom-sheet.component';
import { PortalContextualHeadingComponent } from '../portal-contextual-heading/portal-contextual-heading.component';

@Component({
	selector: 'app-sidenav',
	templateUrl: './sidenav.component.html',
	styleUrl: './sidenav.component.scss',
	standalone: true,
	imports: [
		MatSidenavModule,
		MatListModule,
		MatIconModule,
		MatButtonModule,
		RouterModule,
		AvatarComponent,
		PortalContextualHeadingComponent,
		CompanySwitcherComponent,
		UikSidenavSubNavigationComponent,
		UikSidenavSubNavigationRefDirective,
		UikSidenavNavigationRouteComponent,
		UikSidenavLogoComponent,
		UikSidenavLayoutDirective,
		UikSubNavigationComponent,
		BookmarkListComponent,
	],
})
export class SidenavComponent implements OnInit, AfterViewInit {
	public breakpointObserver = inject(BreakpointObserver);
	public router = inject(Router);
	public storeService = inject(StoreService);
	public accountService = inject(AccountService);
	private readonly bottomSheet = inject(MatBottomSheet);
	private readonly localeId: string = inject(LOCALE_ID);
	private readonly unsubscribeSubject = new Subject<void>();
	private readonly uikSidenavService = inject(UikSidenavService);
	public isDesktop = false;
	public isLaptop = false;
	public locale: string;
	public firstname: string;
	public lastname: string;
	public picture: string;
	public customerNumber: string | null = null;

	public activeLink: Link = {
		name: $localize`:@@hub.global.home-label:Accueil`,
		url: '/home',
		icon: 'home',
		subLinks: [],
	};

	public contextualHeading_headlines: { routing: string; title: string }[] = [
		{
			routing: '/',
			title: $localize`:@@hub.global.home-label:Accueil`,
		},
		{
			routing: '/home',
			title: $localize`:@@hub.global.home-label:Accueil`,
		},
		{
			routing: '/my_information',
			title: $localize`:@@hub.global.my-information-label:Mes informations`,
		},
		{
			routing: '/my_company',
			title: $localize`:@@hub.global.my-company-label:Ma société`,
		},
		{
			routing: '/my_collaborators',
			title: $localize`:@@hub.global.my-collaborators-label:Mes collaborateurs`,
		},
		{
			routing: '/my_communities',
			title: $localize`:@@hub.global.communities-label:Communautés`,
		},
		{
			routing: '/support',
			title: $localize`:@@hub.global.client-service-label:Service client`,
		},
		{
			routing: '/helpdesk/tickets',
			title: $localize`:@@hub.global.client-service-label:Service client`,
		},
		{
			routing: '/projects/flexo',
			title: $localize`:@@hub.global.flexo-project-label:Projets Flexo`,
		},
		{
			routing: '/download-center',
			title: $localize`:@@hub.global.download-center-label:Centre de téléchargement`,
		},
		{
			routing: '/menu',
			title: '',
		},
		{
			routing: '/legal_documents',
			title: $localize`:@@hub.global.legal-documents-label:Documents légaux`,
		},
		{
			routing: '/legal_documents/cgu',
			title: $localize`:@@hub.global.legal-documents-label:Documents légaux`,
		},
		{
			routing: '/legal_documents/legal_notice',
			title: $localize`:@@hub.global.legal-documents-label:Documents légaux`,
		},
		{
			routing: '/legal_documents/privacy_policy',
			title: $localize`:@@hub.global.legal-documents-label:Documents légaux`,
		},
		{
			routing: '/page-not-found',
			title: '',
		},
		{
			routing: '/users',
			title: $localize`:@@hub.global.users-label:Utilisateurs`,
		},
		{
			routing: '/licences',
			title: $localize`:@@hub.global.licences-label:Licences`,
		},
		{
			routing: '/contracts',
			title: $localize`:@@hub.global.contracts-label:Contrats`,
		},
		{
			routing: '/orders',
			title: $localize`:@@digital-i18n.menu.orders-label:Commandes`,
		},
		{
			routing: '/training/catalog',
			title: $localize`:@@hub.global.training-center-label:Centre de formation`,
		},
		{
			routing: '/training/calendar',
			title: $localize`:@@hub.global.training-calendar-label:Calendrier des formations`,
		},
		{
			routing: '/my-sessions',
			title: $localize`:@@hub.global.training-my-sessions-label:Mes sessions`,
		},
	];
	public navLinks: Link[] = [
		{
			name: $localize`:@@hub.global.home-label:Accueil`,
			url: '/home',
			icon: 'home',
			subLinks: [],
		},
		{
			name: $localize`:@@hub.global.client-service-label:Service client`,
			url: '/helpdesk/tickets',
			icon: 'support_agent',
			subLinks: [
				{
					name: $localize`:@@hub.global.helpdesk-label:Mes demandes d'assistance`,
					url: '/helpdesk/tickets',
					icon: '',
				},
				{
					name: $localize`:@@hub.global.flexo-project-label:Projets Flexo`,
					url: '/projects/flexo',
					icon: '',
				},
				{
					name: $localize`:@@hub.global.download-center-label:Centre de téléchargement`,
					url: '/download-center',
					icon: '',
				},
			],
			ref: 'helpdesk',
		},
		{
			name: $localize`:@@hub.global.my-company-label:Ma société`,
			url: '/my_company',
			icon: 'domain',
			subLinks: [
				{
					name: $localize`:@@hub.global.company-information-label:Informations société`,
					url: '/my_company',
					icon: '',
				},
				{
					name: $localize`:@@hub.global.users-label:Utilisateurs`,
					url: '/users',
					icon: '',
				},
				{
					name: $localize`:@@hub.global.licences-label:Licences`,
					url: '/licences',
					icon: '',
				},
				{
					name: $localize`:@@hub.global.contracts-label:Contrats`,
					url: '/contracts',
					icon: '',
				},
			],
			ref: 'company',
		},
	];

	constructor() {
		this.locale = this.localeId;

		this.firstname = this.storeService.connectedUser?.firstname;
		this.lastname = this.storeService.connectedUser?.lastname;
		this.picture = this.storeService.connectedUser?.picture;

		this.uikSidenavService.changeSidenavLayout('mini');

		if (this.locale !== 'de' && this.locale !== 'pl') {
			this.navLinks.push({
				name: $localize`:@@hub.global.my-projects-label:Projets`,
				url: '/projects',
				icon: 'home_storage',
				subLinks: [],
			});

			this.contextualHeading_headlines.push({
				routing: '/projects',
				title: $localize`:@@hub.global.my-projects-label:Projets`,
			});
		}

		this.navLinks.push({
			name: $localize`:@@digital-i18n.menu.orders-label:Commandes`,
			url: '/orders',
			icon: 'shopping_basket',
			subLinks: [],
		});

		if (this.locale === 'fr') {
			this.navLinks.push({
				name: $localize`:@@hub.global.training-center-label:Centre de formation`,
				url: '/training/catalog',
				icon: 'school',
				subLinks: [
					{
						name: $localize`:@@hub.global.training-catalog-label:Toutes les formations`,
						url: '/training/catalog',
						icon: '',
					},
					{
						name: $localize`:@@hub.global.training-calendar-label:Calendrier des formations`,
						url: '/training/calendar',
						icon: '',
					},
					{
						name: $localize`:@@hub.global.training-my-sessions-label:Mes sessions`,
						url: '/my-sessions',
						icon: '',
					},
					{
						name: '',
						url: '/training/course',
						icon: '',
					},
				],
				ref: 'training',
			});
		}

		this.accountService.tokenStoredUserSubject
			.pipe(takeUntil(this.unsubscribeSubject))
			.subscribe(tokenClaims => {
				if (tokenClaims?.guid) {
					const supportNavLink = this.navLinks.find(
						navLink =>
							navLink.name === $localize`:@@hub.global.client-service-label:Service client`,
					);
					if (supportNavLink) {
						if (
							!this.accountService.connectedUserToken?.customerId ||
							!this.accountService.connectedUserToken?.idContactSalesforce
						) {
							supportNavLink.url = '/helpdesk/support';
						} else {
							supportNavLink.url = '/helpdesk/tickets';
						}
					}
				}

				if (tokenClaims && !tokenClaims?.customerId) {
					const myProfileNavLink = this.navLinks.findIndex(
						navLink => navLink.name === $localize`:@@hub.global.profile-label:Mon profil`,
					);
					const myCompanyNavLink = this.navLinks.findIndex(
						navLink => navLink.name === $localize`:@@hub.global.my-company-label:Ma société`,
					);
					if (myProfileNavLink >= 0) {
						this.navLinks[myProfileNavLink].subLinks = [
							{
								name: $localize`:@@hub.global.my-information-label:Mes informations`,
								url: '/my_information',
								icon: '',
							},
						];
					}

					if (myCompanyNavLink >= 0) {
						this.navLinks[myCompanyNavLink].subLinks = [];
						this.navLinks[myCompanyNavLink].ref = undefined;
					}
				}
			});
	}

	ngOnInit(): void {
		if (this.locale !== 'pl' && this.locale !== 'nl') {
			this.navLinks.push({
				name: $localize`:@@hub.global.communities-label:Communautés`,
				url: '/my_communities',
				icon: 'people_alt',
				subLinks: [],
			});
		}
		this.breakpointObserver
			.observe([Breakpoints.Medium, Breakpoints.Large, Breakpoints.XLarge, '(max-width: 1600px)'])
			.subscribe(result => {
				this.isDesktop =
					result.breakpoints[Breakpoints.Medium] ||
					result.breakpoints[Breakpoints.Large] ||
					result.breakpoints[Breakpoints.XLarge];
				this.isLaptop = result.breakpoints['(max-width: 1600px)'];
			});

		this.getUserData().subscribe(({ user, company }) => {
			this.firstname = user?.firstname;
			this.lastname = user?.lastname;
			this.picture = user?.picture;
			this.customerNumber = company?.netsuiteClientNumber;
		});
	}

	ngAfterViewInit(): void {
		this.router.events.subscribe(event => {
			if (event instanceof NavigationEnd) {
				this.findLinkByUrl(event.urlAfterRedirects);
			}
		});
	}

	private getUserData(): Observable<{
		user: Profile | null;
		company: Company | null;
	}> {
		const user$: Observable<Profile | null> =
			this.accountService.profileInformations?.guid !== ''
				? of(this.accountService.profileInformations)
				: this.accountService.getProfile().pipe(catchError(() => of(null)));

		const company$: Observable<Company | null> = this.accountService.userHasCompany()
			? this.accountService.companyInformations?.legalName
				? of(this.accountService.companyInformations)
				: this.accountService.getCompany().pipe(catchError(() => of(null)))
			: of(null);

		return forkJoin({
			profile: user$,
			company: company$,
		}).pipe(
			map(({ profile, company }) => ({
				user: profile,
				company: company,
			})),
		);
	}

	public openProfileMenu(): void {
		this.bottomSheet.open(ProfileMenuBottomSheetComponent, {
			panelClass: 'profile-menu__bottom-sheet',
		});
	}

	public findLinkByUrl(url: string): void {
		const currentUrl = url.split('?')[0];
		const matchedLink = this.navLinks.find(
			link =>
				currentUrl === link.url ||
				currentUrl.startsWith(`${link.url}/`) ||
				link.subLinks.some(
					subLink => currentUrl === subLink.url || currentUrl.startsWith(`${subLink.url}/`),
				),
		);

		if (matchedLink) {
			this.activeLink = matchedLink;
			return;
		}

		this.activeLink = {
			name: $localize`:@@digital-i18n.menu.home-label:Accueil`,
			url: '/home',
			icon: 'home',
			subLinks: [],
		};
	}

	public isLinkActive(link: Link): boolean {
		return isActiveLink(this.router, link.url, link.subLinks);
	}
}
